import React, { useRef, useState, useEffect, ChangeEvent, CSSProperties } from 'react';
import { useToaster } from 'contexts/ToasterContext';
import { ImageInputPopUp } from './ImageInputPopUp';
import { LinkInputPopUp } from './LinkInputPopUp';
import { Color } from '@tiptap/extension-color';
import { TextStyle, FontSize, BackgroundColor } from '@tiptap/extension-text-style';
import { useCurrentEditor, useEditorState, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Node, mergeAttributes } from '@tiptap/core';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import { getHierarchicalIndexes, TableOfContents } from '@tiptap/extension-table-of-contents';
import {
  Button,
  Stack,
  Box,
  Paper,
  IconButton,
  Tooltip,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  StrikethroughS,
  Code as CodeIcon,
  FormatClear,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  HorizontalRule as HorizontalRuleIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
  Image as ImageIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  FormatSize,
  FormatColorText,
  FormatColorFill,
  FormatIndentIncrease,
  AspectRatio,
  Link as LinkIcon,
} from '@mui/icons-material';

const Image = Node.create({
  name: 'image',
  group: 'block',
  draggable: true,
  selectable: true,
  inline: false,

  addAttributes() {
    return {
      src: {
        default: null,
      },
      alt: {
        default: null,
      },
      title: {
        default: null,
      },
      width: {
        default: 400,
        parseHTML: (element: HTMLElement) => element.getAttribute('width'),
        renderHTML: (attributes: Record<string, any>) =>
          attributes.width
            ? {
                width: attributes.width,
              }
            : {},
      },
      height: {
        default: 300,
        parseHTML: (element: HTMLElement) => element.getAttribute('height'),
        renderHTML: (attributes: Record<string, any>) =>
          attributes.height
            ? {
                height: attributes.height,
              }
            : {},
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'img[src]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }: { HTMLAttributes: Record<string, any> }) {
    const { style, ...rest } = HTMLAttributes;

    return [
      'div',
      {
        style: style || '',
      },
      ['img', mergeAttributes(rest)],
    ];
  },

  addNodeView() {
    return ({ node, editor }) => {
      const dom = document.createElement('div');
      dom.classList.add('resizable-image-node');
      dom.style.display = 'block';

      const align = (node.attrs as any).textAlign;
      if (align === 'center' || align === 'right') {
        dom.style.textAlign = align;
      } else {
        dom.style.textAlign = 'left';
      }

      const inner = document.createElement('div');
      inner.classList.add('resizable-image-inner');
      inner.style.display = 'inline-block';
      inner.style.position = 'relative';

      const img = document.createElement('img');
      img.src = node.attrs.src;
      img.alt = node.attrs.alt || '';
      img.title = node.attrs.title || '';
      img.style.display = 'block';

      const setSizeFromAttrs = (attrs: any) => {
        const { width, height } = attrs;
        if (width) {
          img.style.width = typeof width === 'number' ? `${width}px` : width;
        } else {
          img.style.removeProperty('width');
        }
        if (height) {
          img.style.height = typeof height === 'number' ? `${height}px` : height;
        } else {
          img.style.removeProperty('height');
        }
      };

      setSizeFromAttrs(node.attrs as any);

      inner.appendChild(img);
      dom.appendChild(inner);

      type ResizeDirection = 'right' | 'left' | 'bottom' | 'top' | 'corner';

      const startResize = (event: MouseEvent, direction: ResizeDirection) => {
        if (!editor?.isEditable) return;

        event.preventDefault();
        event.stopPropagation();

        const startX = event.clientX;
        const startY = event.clientY;
        const rect = img.getBoundingClientRect();
        const startWidth = rect.width;
        const startHeight = rect.height;
        const minWidth = 50;
        const minHeight = 50;

        const onMove = (moveEvent: MouseEvent) => {
          let newWidth = startWidth;
          let newHeight = startHeight;

          if (direction === 'right' || direction === 'corner') {
            newWidth = Math.max(minWidth, startWidth + (moveEvent.clientX - startX));
          }
          if (direction === 'bottom' || direction === 'corner') {
            newHeight = Math.max(minHeight, startHeight + (moveEvent.clientY - startY));
          }
          if (direction === 'left') {
            newWidth = Math.max(minWidth, startWidth - (moveEvent.clientX - startX));
          }
          if (direction === 'top') {
            newHeight = Math.max(minHeight, startHeight - (moveEvent.clientY - startY));
          }

          editor?.commands.updateAttributes('image', {
            width: Math.round(newWidth),
            height: Math.round(newHeight),
          });
        };

        const onUp = () => {
          window.removeEventListener('mousemove', onMove);
          window.removeEventListener('mouseup', onUp);
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
      };

      const createHandle = (className: string, direction: ResizeDirection) => {
        const handle = document.createElement('span');
        handle.classList.add('resize-handle', className);
        handle.addEventListener('mousedown', (event) =>
          startResize(event as MouseEvent, direction)
        );
        return handle;
      };

      inner.appendChild(createHandle('resize-handle-right', 'right'));
      inner.appendChild(createHandle('resize-handle-bottom', 'bottom'));
      inner.appendChild(createHandle('resize-handle-corner', 'corner'));
      inner.appendChild(createHandle('resize-handle-left', 'left'));
      inner.appendChild(createHandle('resize-handle-top', 'top'));

      return {
        dom,
        update(updatedNode) {
          if (updatedNode.type.name !== 'image') {
            return false;
          }

          const attrs = updatedNode.attrs as any;
          if (attrs.src !== img.src) {
            img.src = attrs.src;
          }
          img.alt = attrs.alt || '';
          img.title = attrs.title || '';

          setSizeFromAttrs(updatedNode.attrs as any);

          const updatedAlign = attrs.textAlign;
          if (updatedAlign === 'center' || updatedAlign === 'right') {
            dom.style.textAlign = updatedAlign;
          } else {
            dom.style.textAlign = 'left';
          }

          return true;
        },
        destroy() {
          // No explicit cleanup needed; DOM subtree and listeners are GC'ed together
        },
      };
    };
  },
});

/**
 * Toolbar / MenuBar to be used with TipTap's EditorProvider.
 * NOTE: This component assumes it's rendered inside an EditorProvider
 * so that `useCurrentEditor()` returns a valid editor instance.
 */
export const MenuBar: React.FC = () => {
  const { editor } = useCurrentEditor();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [fontSize, setFontSize] = useState<number>(16); // px
  const [textColor, setTextColor] = useState<string>('#000000');
  const [highlightColor, setHighlightColor] = useState<string>('#ffff00');
  const [blockType, setBlockType] = useState<string>('paragraph');
  const [isAddImagePopupOpen, setIsAddImagePopupOpen] = useState(false);
  const [isAddLinkPopupOpen, setIsAddLinkPopupOpen] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [selectedUrl, setSelectedUrl] = useState('');
  const { triggerToast } = useToaster();

  const { canUndo, canRedo } = useEditorState({
    editor,
    selector: (ctx) => {
      return {
        canUndo: ctx.editor.can().chain().focus().undo().run(),
        canRedo: ctx.editor.can().chain().focus().redo().run(),
      };
    },
  });

  // Update blockType state based on current selection
  useEffect(() => {
    if (!editor) return;

    const updateBlockType = () => {
      if (editor.isActive('paragraph')) {
        setBlockType('paragraph');
      } else if (editor.isActive('codeBlock')) {
        setBlockType('code-block');
      } else {
        for (let i = 1; i <= 6; i++) {
          if (editor.isActive('heading', { level: i as 1 | 2 | 3 | 4 | 5 | 6 })) {
            setBlockType(`heading-${i}`);
            break;
          }
        }
      }
    };

    // Update on selection change
    editor.on('selectionUpdate', updateBlockType);
    // Update on document change
    editor.on('update', updateBlockType);

    // Initial update
    updateBlockType();

    return () => {
      editor.off('selectionUpdate', updateBlockType);
      editor.off('update', updateBlockType);
    };
  }, [editor]);

  if (!editor) {
    return null;
  }

  // Add link functionality
  const addLink = () => {
    const { state } = editor;
    const { from, to } = state.selection;
    const text = state.doc.textBetween(from, to, '');
    const linkAttrs = editor.getAttributes('link') || {};
    const existingHref = linkAttrs.href || '';
    setSelectedText(text);
    setSelectedUrl(existingHref);
    setIsAddLinkPopupOpen(true);
  };

  const handleAddLinkSubmit = (url: string, text?: string) => {
    const trimmedText = text?.trim() || '';

    if (selectedText) {
      // There is selected text in the editor
      if (trimmedText) {
        // Replace selection with the provided link text
        editor
          .chain()
          .focus()
          .deleteSelection()
          .insertContent(`<a href="${url}">${trimmedText}</a>`)
          .run();
      } else {
        // No explicit link text provided → just apply/update link mark on the selection
        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
      }
    } else {
      // No selection in editor → insert a new link with provided or fallback text
      const linkText = trimmedText || url;

      editor.chain().focus().insertContent(`<a href="${url}">${linkText}</a>`).run();
    }

    triggerToast({
      message: 'Link added successfully',
      type: 'success',
    });

    // Clear local selection metadata once we are done
    setSelectedText('');
    setSelectedUrl('');
  };

  const handleClearLink = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();

    triggerToast({
      message: 'Link removed',
      type: 'success',
    });

    setSelectedText('');
    setSelectedUrl('');
  };

  // Add image with URL + width + height
  const addImage = () => {
    setIsAddImagePopupOpen(true);
  };

  const handleAddImageSubmit = (url: string) => {
    editor
      .chain()
      .focus()
      .insertContent({
        type: 'image',
        attrs: {
          src: url,
          alt: '',
          title: '',
        },
      })
      .run();

    triggerToast({
      message: 'Image added successfully',
      type: 'success',
    });
  };

  // Font size: increase / decrease using FontSize extension
  const changeFontSize = (delta: number) => {
    setFontSize((prev) => {
      let next = prev + delta;
      if (next < 8) next = 8;
      if (next > 72) next = 72;

      editor.chain().focus().setFontSize(`${next}px`).run();
      return next;
    });
  };

  // "Tab" – insert some space at cursor (like indent)
  const insertTab = () => {
    editor.chain().focus().insertContent('\u00A0\u00A0\u00A0\u00A0').run(); // 4 non-breaking spaces
  };
  /*
  // upload download functions currently not required but is useful for debugging purpose so kindly keep it
  // 3 placeses to uncomment for use : 1. handler 2. file input field 3. buttons in menu bar
  const handleDownloadJson = () => {
    if (!editor) return;

    try {
      const json = editor.getJSON();
      const jsonString = JSON.stringify(json, null, 2);

      const blob = new Blob([jsonString], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = 'document.json';

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Download failed, opening JSON in new tab', e);

      const json = editor.getJSON();
      const jsonString = JSON.stringify(json, null, 2);
      const blob = new Blob([jsonString], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    }
  };

  const handleUploadJson = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = reader.result as string;
        const json = JSON.parse(text);
        editor.commands.setContent(json);
      } catch (error) {
        console.error('Invalid JSON file', error);
        triggerToast({
          message: 'Invalid JSON file',
          type: 'error',
        });
      }
    };
    reader.readAsText(file);

    // allow selecting same file again
    event.target.value = '';
  };

  const triggerUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
*/

  const handleTextColorChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setTextColor(value);
    editor.chain().focus().setColor(value).run();
  };

  const handleHighlightColorChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setHighlightColor(value);
    editor.chain().focus().setBackgroundColor(value).run();
  };

  const handleBlockTypeChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    setBlockType(value);

    if (value === 'paragraph') {
      editor.chain().focus().setParagraph().run();
    } else if (value === 'code-block') {
      editor.chain().focus().toggleCodeBlock().run();
    } else if (value.startsWith('heading-')) {
      const level = parseInt(value.split('-')[1]) as 1 | 2 | 3 | 4 | 5 | 6;
      editor.chain().focus().toggleHeading({ level }).run();
    }
  };

  const colorInputStyle: CSSProperties = {
    width: 32,
    height: 32,
    padding: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
  };

  // Compact menu bar layout similar to the provided image
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        mb: 1,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          px: 1,
          py: 0.5,
          width: '100%',
          backgroundColor: '#1e1e1e', // Dark background like in the image
          color: 'white',
          borderRadius: 0,
        }}
      >
        {/* Hidden file input for JSON upload */}
        {/* <input
          type="file"
          accept="application/json"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleUploadJson}
        /> */}
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          flexWrap="wrap"
          divider={
            <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
          }
        >
          {/* Group 1: History */}
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title="Undo">
              <span>
                <IconButton
                  size="small"
                  sx={{
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.1)',
                    },
                    '&.Mui-disabled': {
                      color: '#878787ff',
                      bgcolor: 'transparent',
                    },
                  }}
                  disabled={!canUndo}
                  onClick={() => editor.chain().focus().undo().run()}
                >
                  <UndoIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Redo">
              <span>
                <IconButton
                  size="small"
                  sx={{
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.1)',
                    },
                    '&.Mui-disabled': {
                      color: '#878787ff',
                      bgcolor: 'transparent',
                    },
                  }}
                  disabled={!canRedo}
                  onClick={() => editor.chain().focus().redo().run()}
                >
                  <RedoIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          {/* Group 2: Heading dropdown */}
          <Select
            value={blockType}
            onChange={handleBlockTypeChange}
            size="small"
            sx={{
              height: 28,
              color: 'white',
              '.MuiOutlinedInput-notchedOutline': { border: 'none' },
              '.MuiSvgIcon-root': { color: 'white' },
              '.MuiOutlinedInput-input': { fontSize: '0.85rem', py: 0.5 },
              bgcolor: 'rgba(255,255,255,0.05)',
              minWidth: 70,
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.1)',
              },
            }}
            displayEmpty
            renderValue={(value) => {
              if (value.startsWith('heading-')) {
                return `H${value.split('-')[1]}`;
              } else if (value === 'paragraph') {
                return 'Normal';
              } else {
                return value;
              }
            }}
          >
            <MenuItem value="paragraph">Normal</MenuItem>
            <MenuItem value="heading-1">Heading 1</MenuItem>
            <MenuItem value="heading-2">Heading 2</MenuItem>
            <MenuItem value="heading-3">Heading 3</MenuItem>
            <MenuItem value="heading-4">Heading 4</MenuItem>
            <MenuItem value="heading-5">Heading 5</MenuItem>
            <MenuItem value="heading-6">Heading 6</MenuItem>
          </Select>

          {/* Group 3: Lists */}
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title="Bullet List">
              <IconButton
                size="small"
                sx={{
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
                onClick={() => editor.chain().focus().toggleBulletList().run()}
              >
                <FormatListBulleted fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Ordered List">
              <IconButton
                size="small"
                sx={{
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
              >
                <FormatListNumbered fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>

          {/* Group 4: Text formatting and link */}
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title="Bold">
              <IconButton
                size="small"
                sx={{
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
                onClick={() => editor.chain().focus().toggleBold().run()}
              >
                <FormatBold fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Italic">
              <IconButton
                size="small"
                sx={{
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
                onClick={() => editor.chain().focus().toggleItalic().run()}
              >
                <FormatItalic fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Add link">
              <IconButton
                size="small"
                sx={{
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
                onClick={addLink}
              >
                <LinkIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Code">
              <IconButton
                size="small"
                sx={{
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
                onClick={() => editor.chain().focus().toggleCode().run()}
              >
                <CodeIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>

          <Tooltip title="Strike">
            <IconButton
              size="small"
              sx={{
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.1)',
                },
              }}
              onClick={() => editor.chain().focus().toggleStrike().run()}
            >
              <StrikethroughS fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Group 5: Alignment */}
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title="Align Left">
              <IconButton
                size="small"
                sx={{
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
                onClick={() => editor.chain().focus().setTextAlign('left').run()}
              >
                <FormatAlignLeft fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Align Center">
              <IconButton
                size="small"
                sx={{
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
                onClick={() => editor.chain().focus().setTextAlign('center').run()}
              >
                <FormatAlignCenter fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Align Right">
              <IconButton
                size="small"
                sx={{
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
                onClick={() => editor.chain().focus().setTextAlign('right').run()}
              >
                <FormatAlignRight fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Justify">
              <IconButton
                size="small"
                sx={{
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
                onClick={() => editor.chain().focus().setTextAlign('justify').run()}
              >
                <FormatAlignJustify fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>

          {/* Group 6: clear and break */}
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title="Clear marks">
              <IconButton
                size="small"
                sx={{
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
                onClick={() => editor.chain().focus().unsetAllMarks().run()}
              >
                <FormatClear fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Clear nodes">
              <IconButton
                size="small"
                sx={{
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
                onClick={() => editor.chain().focus().clearNodes().run()}
              >
                <FormatClear fontSize="small" sx={{ opacity: 0.7 }} />
              </IconButton>
            </Tooltip>

            {/* Blockquote and Horizontal rule */}
            <Tooltip title="Blockquote">
              <IconButton
                size="small"
                sx={{
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
                onClick={() => editor.chain().focus().toggleBlockquote().run()}
              >
                <FormatQuote fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Horizontal rule">
              <IconButton
                size="small"
                sx={{
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
                onClick={() => editor.chain().focus().setHorizontalRule().run()}
              >
                <HorizontalRuleIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Break button */}
            {/* <Button
              size="small"
              variant="text"
              sx={{
                color: 'white',
                textTransform: 'none',
                minWidth: 'auto',
                px: 1,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.1)',
                },
              }}
              onClick={() => editor.chain().focus().setHardBreak().run()}
            >
              Break
            </Button> */}
          </Stack>

          {/* Group 7: color icons */}
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title="Text color">
              <IconButton size="small" component="label" sx={{ color: 'white' }}>
                <FormatColorText fontSize="small" />
                <input
                  type="color"
                  value={textColor}
                  onChange={handleTextColorChange}
                  style={{
                    ...colorInputStyle,
                    width: 16,
                    height: 16,
                    padding: 0,
                  }}
                />
              </IconButton>
            </Tooltip>
            <Tooltip title="Highlight color">
              <IconButton size="small" component="label" sx={{ color: 'white' }}>
                <FormatColorFill fontSize="small" />
                <input
                  type="color"
                  value={highlightColor}
                  onChange={handleHighlightColorChange}
                  style={{
                    ...colorInputStyle,
                    width: 16,
                    height: 16,
                    padding: 0,
                  }}
                />
              </IconButton>
            </Tooltip>
          </Stack>

          {/* Group 8: Font size controls */}
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title="Decrease font size">
              <IconButton
                size="small"
                sx={{
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
                onClick={() => changeFontSize(-2)}
              >
                <FormatSize sx={{ fontSize: 16 }} />
                <span
                  style={{
                    marginLeft: 2,
                    fontSize: 10,
                    color: 'white',
                  }}
                >
                  -
                </span>
              </IconButton>
            </Tooltip>
            <span
              style={{
                fontSize: 12,
                color: 'white',
                minWidth: 24,
                textAlign: 'center',
              }}
            >
              {fontSize}px
            </span>
            <Tooltip title="Increase font size">
              <IconButton
                size="small"
                sx={{
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
                onClick={() => changeFontSize(2)}
              >
                <FormatSize sx={{ fontSize: 18 }} />
                <span style={{ marginLeft: 2, fontSize: 10, color: 'white' }}>+</span>
              </IconButton>
            </Tooltip>

            <Tooltip title="Tab space">
              <IconButton
                size="small"
                sx={{
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
                onClick={insertTab}
              >
                <FormatIndentIncrease fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>

          {/* Group 9: Image and JSON operations */}
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title="Add image">
              <IconButton
                size="small"
                sx={{
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
                onClick={addImage}
              >
                <ImageIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Image Input Popups */}
            <ImageInputPopUp
              isOpen={isAddImagePopupOpen}
              close={() => setIsAddImagePopupOpen(false)}
              onSubmit={handleAddImageSubmit}
              title="Add Image"
            />

            <LinkInputPopUp
              isOpen={isAddLinkPopupOpen}
              close={() => {
                setIsAddLinkPopupOpen(false);
                setSelectedText('');
                setSelectedUrl('');
              }}
              onSubmit={handleAddLinkSubmit}
              initialUrl={selectedUrl}
              initialText={selectedText}
              title="Add Link"
              onClear={() => handleClearLink()}
            />
            {/* <Tooltip title="Save as JSON">
              <IconButton
                size="small"
                sx={{
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
                onClick={handleDownloadJson}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
                      </Tooltip>
            <Tooltip title="Upload JSON">
              <IconButton
                size="small"
                sx={{
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
                onClick={triggerUpload}
              >
                <UploadIcon fontSize="small" />
              </IconButton>
            </Tooltip> */}
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
};
export type TocItem = {
  id: string;
  text: string;
  level: number;
  type: 'heading' | 'listItem';
  occurrence: number;
};

const extractTextFromNode = (node: any): string => {
  if (!node) return '';
  if (node.type === 'text' && typeof node.text === 'string') {
    return node.text;
  }
  if (!Array.isArray(node.content)) return '';
  return node.content.map((child: any) => extractTextFromNode(child)).join('');
};

// For list items we only want the "title" – typically the first paragraph line
const extractListItemTitleFromJson = (listItem: any): string => {
  if (!listItem || !Array.isArray(listItem.content)) return '';
  const firstParagraph = listItem.content.find((child: any) => child.type === 'paragraph');
  if (!firstParagraph) return '';

  const boldText =
    Array.isArray(firstParagraph.content) &&
    firstParagraph.content
      .filter(
        (child: any) =>
          child?.type === 'text' &&
          Array.isArray(child.marks) &&
          child.marks.some((mark: any) => mark?.type === 'bold'),
      )
      .map((child: any) => child.text || '')
      .join('')
      .trim();

  if (boldText) return boldText;

  return extractTextFromNode(firstParagraph).trim();
};

/**
 * Build a hierarchical TOC from the ProseMirror JSON doc.
 *
 * – Headings become top-level / section entries.
 * – Ordered list items under a heading become nested entries under that heading.
 * – Nested ordered lists become deeper levels.
 */
export const buildTocFromContent = (doc: any | null | undefined): TocItem[] => {
  if (!doc || typeof doc !== 'object' || !Array.isArray((doc as any).content)) return [];

  const acc: TocItem[] = [];
  let lastHeadingLevel = 0;
  let idCounter = 0;
  const textCounts = new Map<string, number>();

  const pushHeading = (node: any) => {
    const level = node.attrs?.level ?? 1;
    const text = extractTextFromNode(node).trim();
    if (!text) return;
    const key = text.toLowerCase();
    const occurrence = textCounts.get(key) ?? 0;
    textCounts.set(key, occurrence + 1);

    acc.push({
      id: `h-${idCounter++}`,
      text,
      level,
      type: 'heading',
      occurrence,
    });
    lastHeadingLevel = level;
  };

  const pushList = (listNode: any, listDepth: number): void => {
    if (!listNode || listNode.type !== 'orderedList' || !Array.isArray(listNode.content)) return;
    const headingLevel = lastHeadingLevel || 1;

    listNode.content.forEach((listItem: any) => {
      if (!listItem || listItem.type !== 'listItem') return;

      const title = extractListItemTitleFromJson(listItem);
      if (title) {
        const key = title.toLowerCase();
        const occurrence = textCounts.get(key) ?? 0;
        textCounts.set(key, occurrence + 1);
        acc.push({
          id: `li-${idCounter++}`,
          text: title,
          // One level deeper than the parent heading, plus list nesting depth
          level: headingLevel + listDepth,
          type: 'listItem',
          occurrence,
        });
      }

      // Look for nested ordered lists inside this list item and treat them as deeper levels
      if (Array.isArray(listItem.content)) {
        listItem.content.forEach((child: any) => {
          if (child && child.type === 'orderedList') {
            pushList(child, listDepth + 1);
          }
        });
      }
    });
  };

  (doc as any).content.forEach((node: any) => {
    if (!node || typeof node !== 'object') return;

    if (node.type === 'heading') {
      pushHeading(node);
      return;
    }

    if (node.type === 'orderedList') {
      // Attach this ordered list to the last seen heading
      pushList(node, 1);
    }
  });

  return acc;
};

/**
 * Scroll to a TOC item using the anchors provided by the TableOfContents extension.
 *
 * This relies on:
 *   – TableOfContents being added to the editor extensions
 *   – `anchorTypes` including both 'heading' and 'listItem'
 */
export const scrollToTocItem = (editor: Editor | null | undefined, item: TocItem): void => {
  if (!editor || !editor.view || !editor.view.dom) return;
  if (typeof window === 'undefined') return;

  const scrollToElementWithOffset = (element: HTMLElement) => {
    const findScrollParent = (el: HTMLElement | null): HTMLElement | Window => {
      let current = el?.parentElement;

      while (current) {
        const style = window.getComputedStyle(current);
        const overflowY = style.overflowY || style.overflow;
        const isScrollable =
          ['auto', 'scroll', 'overlay'].includes(overflowY) &&
          current.scrollHeight > current.clientHeight;

        if (isScrollable) {
          return current;
        }

        current = current.parentElement;
      }

      return window;
    };

    const parent = findScrollParent(element);
    const isWindowParent = parent === window;
    const parentRect = isWindowParent
      ? { top: 0 }
      : (parent as HTMLElement).getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const viewportHeight = isWindowParent
      ? window.innerHeight
      : (parent as HTMLElement).clientHeight;
    const desiredOffset = Math.max(0, viewportHeight * 0.14);

    const elementTopWithinParent = elementRect.top - parentRect.top;
    const currentScroll = isWindowParent ? window.scrollY : (parent as HTMLElement).scrollTop;
    const targetScrollTop = Math.max(0, currentScroll + elementTopWithinParent - desiredOffset);

    if (isWindowParent) {
      window.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
    } else {
      (parent as HTMLElement).scrollTo({ top: targetScrollTop, behavior: 'smooth' });
    }
  };

  const storage: any = (editor as any).storage?.tableOfContents;
  const anchors: any[] | undefined = storage?.anchors;
    if (!anchors || !Array.isArray(anchors) || anchors.length === 0) {
    return;
  }

  const getListItemOwnText = (element: HTMLElement | null): string => {
    if (!element) return '';
    let text = '';
    element.childNodes.forEach((node) => {
      if (node.nodeType === 3 /* TEXT_NODE */) {
        text += node.textContent || '';
      } else if (
        node.nodeType === 1 /* ELEMENT_NODE */ &&
        !['OL', 'UL'].includes((node as HTMLElement).tagName)
      ) {
        text += (node as HTMLElement).textContent || '';
      }
    });
    return text.trim();
  };

  const getAnchorLabel = (anchor: any): string => {
    if (!anchor || !anchor.node) return '';
    const domEl = anchor.dom as HTMLElement | undefined;
    if (domEl && domEl.tagName?.toLowerCase() === 'li') {
      const liText = getListItemOwnText(domEl);
      if (liText) return liText;
    }
    return (anchor.textContent || '').trim();
  };

  const isListItemAnchor = (anchor: any): boolean => {
    return anchor.node?.type?.name === 'listItem' && !!anchor.dom?.closest('ol,ul');
  };

  const labelLower = item.text.trim().toLowerCase();
  const targetOccurrence = item.occurrence ?? 0;

  const matchAnchor = (anchor: any): boolean => {
    if (!anchor) return false;
    const label = getAnchorLabel(anchor).toLowerCase();
    if (!label || label !== labelLower) return false;
    if (item.type === 'heading') return anchor.node?.type?.name === 'heading';
    if (item.type === 'listItem') return isListItemAnchor(anchor);
    return false;
  };

  let occurrenceCounter = 0;
  let target: any = null;
  for (const anchor of anchors) {
    if (matchAnchor(anchor)) {
      if (occurrenceCounter === targetOccurrence) {
        target = anchor;
        break;
      }
      occurrenceCounter += 1;
    }
  }

  if (!target) {
    const editorElement = editor.view.dom;
    if (editorElement) {
      const allHeadings = editorElement.querySelectorAll('h1, h2, h3, h4, h5, h6, li');
      let seenCount = 0;
      for (let i = 0; i < allHeadings.length; i++) {
        const heading = allHeadings[i] as HTMLElement;
        const text =
          heading.tagName.toLowerCase() === 'li'
            ? getListItemOwnText(heading)
            : heading.textContent?.trim() || '';
        if (text.toLowerCase() === labelLower) {
          if (seenCount === targetOccurrence) {
            scrollToElementWithOffset(heading as HTMLElement);
            return;
          }
          seenCount += 1;
        }
      }

      seenCount = 0;
      for (let i = 0; i < allHeadings.length; i++) {
        const heading = allHeadings[i] as HTMLElement;
        const text =
          heading.tagName.toLowerCase() === 'li'
            ? getListItemOwnText(heading)
            : heading.textContent?.trim() || '';
        if (text.toLowerCase().includes(labelLower)) {
          if (seenCount === targetOccurrence) {
            scrollToElementWithOffset(heading as HTMLElement);
            return;
          }
          seenCount += 1;
        }
      }
    }

    return;
  }

  // Use the editor commands so scrolling respects the editor's scrollParent
  try {
    if (target.dom) {
      // Prefer using the DOM element directly for more reliable scrolling
      scrollToElementWithOffset(target.dom as HTMLElement);
    } else if (typeof target.pos === 'number') {
      editor.chain().setTextSelection(target.pos).scrollIntoView().run();
    }
  } catch (e) {
    console.error('Failed to scroll to TOC item', e);
  }
};

/**
 * TipTap extensions shared between the editor and the preview.
 */
export const extensions = [
  Color.configure({ types: [TextStyle.name] }),
  BackgroundColor.configure({ types: [TextStyle.name] }),
  TextStyle,
  FontSize.configure({
    // apply font size via TextStyle mark (default is ['textStyle'])
    types: ['textStyle'],
  }),
  StarterKit.configure({
    bulletList: {
      keepMarks: true,
      keepAttributes: false,
    },
    orderedList: {
      keepMarks: true,
      keepAttributes: false,
    },
    link: false,
  }),
  TextAlign.configure({
    types: ['heading', 'paragraph', 'image'], // alignment works for text + images
  }),
  Placeholder.configure({
    placeholder: 'Write something …',
    emptyEditorClass: 'is-editor-empty',
  }),
  Link.configure({
    openOnClick: false, // don't open links when clicked in the editor
    linkOnPaste: true, // automatically turn pasted URLs into links
    HTMLAttributes: {
      rel: 'noopener noreferrer',
      target: '_blank',
    },
  }),
  Image,
  TableOfContents.configure({
    getIndex: getHierarchicalIndexes,
    anchorTypes: ['heading', 'listItem'],
  }),
];
